import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

function firstName(name?: string | null): string {
  return name?.trim().split(/\s+/)[0] ?? "";
}

export function FirstRunGuide({ name }: { name?: string | null }) {
  const first = firstName(name);
  return (
    <div className="mx-auto max-w-md pt-2">
      <h1 className="type-title">{first ? `Welcome, ${first}` : "Welcome"}</h1>
      <p className="text-muted-foreground type-body mt-2">Your first parcel takes three steps.</p>

      <ol className="mt-8">
        <Step number={1} current title="Copy your shipping address">
          <p>A warehouse address abroad that carries your personal code. Use it at the store's checkout.</p>
          <Button asChild size="touch" className="mt-4 w-full">
            <Link to="/addresses">
              Get my address
              <ArrowRight />
            </Link>
          </Button>
        </Step>
        <Step number={2} title="Shop as usual">
          <p>The store delivers to our warehouse and gives you a tracking number.</p>
        </Step>
        <Step number={3} last title="Add your tracking number">
          <p>Tell us what is coming. We ship it to Kenya, clear customs, and deliver it to you.</p>
          <Link
            to="/register"
            className="text-foreground mt-3 inline-flex min-h-11 items-center gap-1 text-[15px] font-medium underline-offset-4 hover:underline"
          >
            I already have a tracking number
            <ArrowRight className="size-4" />
          </Link>
        </Step>
      </ol>
    </div>
  );
}

function Step({
  number,
  title,
  current,
  last,
  children,
}: {
  number: number;
  title: string;
  current?: boolean;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center">
        <span
          className={cn(
            "tabular flex size-8 shrink-0 items-center justify-center rounded-full text-[14px] font-semibold",
            current ? "bg-primary text-primary-foreground" : "border-hairline text-muted-foreground border",
          )}
        >
          {number}
        </span>
        {!last && <span className="bg-hairline my-1 w-px flex-1" aria-hidden />}
      </div>
      <div className={cn("min-w-0 flex-1 pt-1", !last && "pb-8")}>
        <h2 className="type-headline">{title}</h2>
        <div className="text-muted-foreground type-body mt-1">{children}</div>
      </div>
    </li>
  );
}
